import { useEffect, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);

  useEffect(() => {
    document.title = '关于我们 - 免费91网页';
  }, []);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '免费91网页',
    url: 'https://www.free91.com/',
    description: '免费91网页官方入口与完整使用指南，为用户提供访问方式、功能介绍、使用技巧及常见问题解答',
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(false);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('mobile_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormSuccess(true);
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9slfa94ne17ei0sdjag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <main className="pt-20">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[{ name: '首页', path: '/' }, { name: 'about' }]}
            />
          </div>

          {/* Brand Introduction */}
          <section className="mb-16">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
              关于免费91网页
            </h1>
            <div className="prose prose-lg max-w-none text-foreground-700 leading-relaxed">
              <p>
                <strong>免费91网页</strong>是一个致力于为用户提供免费91网页访问指南、功能介绍和使用教程的综合性信息平台。我们的使命是帮助每一位用户快速、安全地找到免费91网页的官方入口，并充分了解和使用平台的全部核心功能。
              </p>
              <p>
                自上线以来，免费91网页导航指南已服务了数百万用户。我们持续跟踪免费91网页的最新动态，及时更新访问方式和功能信息，确保用户获取到的内容始终是最新、最准确的。无论是初次接触的新用户，还是寻求进阶技巧的老用户，都能在这里找到有价值的信息。
              </p>
              <h2 className="text-xl font-semibold font-heading text-foreground-900 mt-8">我们的理念</h2>
              <p>
                我们相信，好的工具应该被更多人轻松使用。免费91网页提供了丰富且实用的在线功能，而我们的职责就是帮助用户克服使用门槛，让每个人都能享受到这些便捷服务。我们坚持提供<strong>免费、准确、易懂</strong>的指南内容。
              </p>
            </div>
          </section>

          {/* Stats / Reputation */}
          <section className="mb-16 py-10 px-6 rounded-lg bg-background-100 border border-background-200/70">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              用户口碑与数据
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: '500万+', label: '累计服务用户' },
                { value: '99.7%', label: '用户满意度' },
                { value: '200+', label: '教程文章' },
                { value: '4.9', label: '用户评分（满分5分）' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold font-heading text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  name: '张先生',
                  role: '自由职业者',
                  content: '免费91网页的教程非常详细，作为新手我很快就学会了使用各种工具，强烈推荐给所有刚接触的朋友！',
                },
                {
                  name: '李女士',
                  role: '企业行政',
                  content: '每天都要用免费91网页处理文档，效率提高了很多。这个导航站帮我找到了很多之前不知道的实用功能。',
                },
                {
                  name: '王同学',
                  role: '大学生',
                  content: '写论文的时候经常用到免费91网页的格式转换功能，太好用了！而且这里的使用教程特别清晰。',
                },
              ].map((review) => (
                <div
                  key={review.name}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    "{review.content}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                    <p className="text-xs text-foreground-400">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                  如果您有任何关于免费91网页使用的问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队会在24小时内回复您的消息。
                </p>
                <div className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@free91.com' },
                    { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00 - 18:00' },
                    { icon: 'ri-map-pin-line', label: '办公地址', value: '中国北京市朝阳区科技园区' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className={item.icon}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">{item.label}</p>
                        <p className="text-sm text-foreground-800">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-4">
                  发送消息
                </h3>
                <form
                  onSubmit={handleSubmit}
                  id="contact-form"
                  data-readdy-form
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="mobile_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden
                    readOnly
                    className="spm-ctrl"
                  />
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                  {formSuccess && (
                    <p className="text-sm text-green-600 text-center">
                      消息已成功发送，我们会尽快回复您！
                    </p>
                  )}
                  {formError && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}